export const LUMA_CAST_NAMESPACE = 'urn:x-cast:com.luma.player';

export const CAST_STATE_MAX_AGE_MS = 5000;
// Rapid skip presses are merged into a single receiver seek (one stream reload).
export const CAST_SEEK_COALESCE_MS = 450;
// A seek the receiver never confirms stops overriding the timeline after this delay.
export const CAST_PENDING_SEEK_MAX_AGE_MS = 15000;
const PENDING_SEEK_REACHED_TOLERANCE = 3;

const asFiniteNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
};

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

export const normalizeCastState = (message, receivedAt = Date.now()) => {
    if (!message || message.type !== 'STATE') return null;

    const duration = Math.max(0, asFiniteNumber(message.duration));
    const maximum = duration > 0 ? duration : Number.POSITIVE_INFINITY;
    const contentId = message.contentId === undefined || message.contentId === null
        ? ''
        : String(message.contentId);

    return {
        currentTime: clamp(asFiniteNumber(message.currentTime), 0, maximum),
        duration,
        isPaused: Boolean(message.isPaused),
        isBuffering: Boolean(message.isBuffering),
        isSeeking: Boolean(message.isSeeking),
        isLive: Boolean(message.isLive),
        isEnded: Boolean(message.isEnded),
        hasError: Boolean(message.hasError),
        contentId,
        playerState: typeof message.playerState === 'string' ? message.playerState : '',
        seekRequestId: typeof message.seekRequestId === 'string' ? message.seekRequestId : '',
        seekError: Boolean(message.seekError),
        sequence: Math.max(0, asFiniteNumber(message.sequence)),
        receivedAt: Math.max(0, asFiniteNumber(receivedAt, Date.now()))
    };
};

export const estimateCastPosition = (state, now = Date.now()) => {
    if (!state) return 0;

    const shouldAdvance = !state.isPaused
        && !state.isBuffering
        && !state.isSeeking
        && !state.isEnded
        && (!state.playerState || state.playerState === 'PLAYING');
    const elapsed = shouldAdvance
        ? Math.max(0, asFiniteNumber(now) - asFiniteNumber(state.receivedAt)) / 1000
        : 0;
    const maximum = state.duration > 0 ? state.duration : Number.POSITIVE_INFINITY;

    return clamp(asFiniteNumber(state.currentTime) + elapsed, 0, maximum);
};

export const isCastStateFresh = (
    state,
    now = Date.now(),
    maxAge = CAST_STATE_MAX_AGE_MS
) => Boolean(state) && Math.max(0, now - state.receivedAt) <= maxAge;

// Decides whether a locally requested seek still owns the timeline once a
// receiver state arrives. Returns the pending seek to keep, or null.
export const reconcilePendingSeek = (pendingSeek, state, now = Date.now()) => {
    if (!pendingSeek) return null;
    if (now - pendingSeek.requestedAt > CAST_PENDING_SEEK_MAX_AGE_MS) return null;
    // Not sent yet (coalescing window): the receiver cannot know about it.
    if (!pendingSeek.sentAt || !state) return pendingSeek;

    if (state.seekRequestId === pendingSeek.requestId && !state.isSeeking) return null;

    const reachedTarget = !state.isSeeking
        && state.receivedAt > pendingSeek.sentAt
        && Math.abs(state.currentTime - pendingSeek.target) < PENDING_SEEK_REACHED_TOLERANCE;
    return reachedTarget ? null : pendingSeek;
};

export const applyPendingSeek = (state, pendingSeek) => {
    if (!state || !pendingSeek) return state;
    return {
        ...state,
        currentTime: pendingSeek.target,
        isSeeking: true,
        isEnded: false
    };
};

// True when two playback snapshots render identically, so the provider can skip
// a React update (the timeline ticks several times per second).
export const isSamePlaybackFrame = (previous, next) => {
    if (!previous || !next) return false;
    return Math.floor(asFiniteNumber(previous.currentTime)) === Math.floor(asFiniteNumber(next.currentTime))
        && Math.round(asFiniteNumber(previous.duration)) === Math.round(asFiniteNumber(next.duration))
        && previous.isPaused === next.isPaused
        && previous.isBuffering === next.isBuffering
        && previous.isSeeking === next.isSeeking
        && previous.isEnded === next.isEnded
        && previous.isMuted === next.isMuted
        && previous.canSeek === next.canSeek
        && Math.round(asFiniteNumber(previous.volume) * 100) === Math.round(asFiniteNumber(next.volume) * 100);
};
